import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SITE } from "../data/content";
import {
  ShieldCheck, FileText, Lock, Building2, Users, PieChart, Calendar,
  CheckCircle2, Clock, ArrowRight, Printer,
} from "lucide-react";

const Accountability = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "Accountability & Transparency | Iron Shield"; }, []);
  const handlePrint = () => window.print();

  return (
    <div className="bg-offwhite min-h-screen pt-[120px] md:pt-[140px] pb-16 print:pt-0" data-testid="accountability-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">

        {/* Header */}
        <div className="bg-white p-8 sm:p-10 rounded-xl border border-softgray brand-card-shadow space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-navy/10 text-navy text-xs font-bold uppercase tracking-widest w-fit">
              <Lock className="w-3.5 h-3.5 text-gold" /> Public Disclosure & Governance
            </div>
            <button onClick={handlePrint} data-testid="accountability-print" className="inline-flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-navy-deep transition-colors w-fit print:hidden">
              <Printer className="w-4 h-4 text-gold" /> Print Disclosure Document
            </button>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-navy">Accountability & Transparency</h1>
          <p className="text-charcoal text-sm sm:text-base leading-relaxed max-w-3xl">
            Iron Shield Veterans Reset Center is committed to building public trust through clear communication, responsible stewardship, and visible accountability. This page serves as the public record of our organizational status, leadership, financial transparency, and program development as we grow.
          </p>
        </div>

        {/* Progress tracker */}
        <div className="bg-white p-6 sm:p-8 rounded-xl border border-softgray brand-card-shadow space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-softgray pb-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[11px] font-bold uppercase tracking-wider border border-amber-200/80 mb-1">
                <Clock className="w-3 h-3 text-amber-600 animate-pulse" /> Active Development Tracker
              </div>
              <h2 className="text-lg font-bold text-navy uppercase tracking-wide">Organizational Readiness & Compliance Progress</h2>
            </div>
            <div className="text-right">
              <span className="text-2xl font-black text-navy">65%</span>
              <span className="text-xs font-bold text-slate block uppercase tracking-wider">Overall Readiness</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="w-full bg-softgray h-3 rounded-full overflow-hidden">
              <div className="bg-gradient-to-r from-navy via-navy-light to-gold h-full rounded-full transition-all duration-1000" style={{ width: "65%" }} />
            </div>
            <div className="flex flex-col sm:flex-row justify-between gap-1 text-[11px] font-semibold text-slate pt-1">
              <span>Phase 1: Formation & Incorporation (85%)</span>
              <span>Phase 2: 501(c)(3) & Legal Filings (60%)</span>
              <span>Phase 3: Public Disclosures & Form 990 (50%)</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            {[
              { title: "State Entity & Incorporation", badge: "Active", badgeCls: "bg-emerald-100 text-emerald-800", bar: "bg-emerald-600", pct: "90%", note: "State filing and organizational structure completed." },
              { title: "501(c)(3) Tax Exemption", badge: "In Progress", badgeCls: "bg-amber-100 text-amber-800 border border-amber-300", bar: "bg-amber-500", pct: "60%", note: "Application pending; disclosure period underway." },
              { title: "Public Disclosures & Audit", badge: "Building", badgeCls: "bg-blue-100 text-blue-800", bar: "bg-blue-600", pct: "45%", note: "Financial models & Form 990 templates in setup." },
            ].map((s) => (
              <div key={s.title} className="p-4 bg-offwhite rounded-lg border border-softgray">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-navy">{s.title}</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.badgeCls}`}>{s.badge}</span>
                </div>
                <div className="w-full bg-softgray h-1.5 rounded-full overflow-hidden mb-1.5">
                  <div className={`${s.bar} h-full rounded-full`} style={{ width: s.pct }} />
                </div>
                <p className="text-[11px] text-slate">{s.note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Promise banner */}
        <div className="bg-navy text-white p-6 sm:p-7 rounded-xl border border-navy-deep flex items-start gap-3.5">
          <ShieldCheck className="w-6 h-6 text-gold flex-shrink-0 mt-0.5" />
          <p className="text-sm sm:text-base font-semibold leading-snug">
            <strong className="text-gold uppercase tracking-wider font-bold">Our promise:</strong> We will communicate clearly, use funds responsibly, and update this page as Iron Shield grows.
          </p>
        </div>

        {/* Organizational status */}
        <section className="bg-white p-8 rounded-xl border border-softgray space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 text-navy">
              <Building2 className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-bold uppercase tracking-wide">Organizational Status</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wide border border-amber-300">
              <Clock className="w-3 h-3 text-amber-600 animate-pulse" /> In Progress Development
            </span>
          </div>
          <p className="text-sm text-charcoal leading-relaxed">
            Iron Shield Veterans Reset Center is being developed as a nonprofit focused on structured housing support and stabilization for veterans in crisis. As formal milestones are completed, this page will be updated with our legal status, tax-exempt documentation, and related public records.
          </p>
          <div className="bg-offwhite p-6 rounded-lg border border-softgray space-y-3">
            <div className="flex items-center justify-between border-b border-softgray pb-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-navy">Public Disclosure & Entity Status Checklist</h3>
              <span className="text-[11px] font-semibold text-amber-700">60% Completed</span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-charcoal">
              {[
                { icon: Clock, iconCls: "text-gold", title: "Legal entity status", sub: "State Nonprofit Incorporation (In Progress / Filing)", badge: "In Progress", badgeCls: "bg-amber-100 text-amber-800 border border-amber-200" },
                { icon: Clock, iconCls: "text-gold", title: "EIN (Employer ID Number)", sub: SITE.ein, badge: "Pending", badgeCls: "bg-amber-100 text-amber-800 border border-amber-200" },
                { icon: Clock, iconCls: "text-gold", title: "501(c)(3) determination status", sub: "Application pending / public disclosure phase", badge: "In Review", badgeCls: "bg-amber-100 text-amber-800 border border-amber-200" },
                { icon: CheckCircle2, iconCls: "text-navy", title: "State registration information", sub: "State Charitable Solicitations Registration", badge: "Active", badgeCls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
              ].map((li) => (
                <li key={li.title} className="flex items-start justify-between bg-white p-3 rounded-lg border border-softgray">
                  <div className="flex items-start gap-2">
                    <li.icon className={`w-4 h-4 ${li.iconCls} flex-shrink-0 mt-0.5`} />
                    <div>
                      <strong className="block text-navy">{li.title}</strong>
                      <span className="text-slate">{li.sub}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full whitespace-nowrap ${li.badgeCls}`}>{li.badge}</span>
                </li>
              ))}
              <li className="flex items-start justify-between bg-white p-3 rounded-lg border border-softgray sm:col-span-2">
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-navy flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="block text-navy">Key filing dates</strong>
                    <span className="text-slate">Milestones updated quarterly as formal filings settle</span>
                  </div>
                </div>
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold uppercase rounded-full whitespace-nowrap">Quarterly Updates</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Leadership & governance */}
        <section className="bg-white p-8 rounded-xl border border-softgray space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 text-navy">
              <Users className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-bold uppercase tracking-wide">Leadership and Governance</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wide border border-amber-300">
              <Clock className="w-3 h-3 text-amber-600 animate-pulse" /> Board Roster In Formation
            </span>
          </div>
          <p className="text-sm text-charcoal leading-relaxed">
            Strong stewardship begins with responsible leadership and oversight. As our board and governance structure are finalized, we will publish leadership information, governance practices, and appropriate contact pathways here.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: "Founder & Executive Leadership", badge: "Active", badgeCls: "text-emerald-700 bg-emerald-100", body: "Veteran-led operational leadership driving facility setup, clinical intake partnerships, and daily veteran stabilization programs." },
              { title: "Board of Directors", badge: "In Formation", badgeCls: "text-amber-800 bg-amber-100 border border-amber-200", body: "Independent oversight board overseeing fiduciary compliance, program integrity, and ethical standards (roster in formation)." },
              { title: "Advisory Partners", badge: "Active", badgeCls: "text-emerald-700 bg-emerald-100", body: "Clinical advisors, VA benefit advocates, and regional housing coordinators offering specialized guidance." },
              { title: "Policies & Inquiries", badge: "Drafting", badgeCls: "text-blue-800 bg-blue-100", body: "Conflict-of-interest policies and direct leadership contact pathways for organizational inquiries." },
            ].map((c) => (
              <div key={c.title} className="p-5 bg-offwhite rounded-lg border border-softgray text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold uppercase tracking-wider text-navy">{c.title}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.badgeCls}`}>{c.badge}</span>
                </div>
                <p className="text-slate leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Financial transparency */}
        <section className="bg-white p-8 rounded-xl border border-softgray space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 text-navy">
              <PieChart className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-bold uppercase tracking-wide">Financial Transparency</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wide border border-amber-300">
              <Clock className="w-3 h-3 text-amber-600 animate-pulse" /> Fiscal Framework In Preparation
            </span>
          </div>
          <p className="text-sm text-charcoal leading-relaxed">
            We are committed to being truthful and clear about how donations are used and how financial decisions support the mission. As records become available, we will publish financial documents and summaries here for public review.
          </p>
          <div className="bg-offwhite p-6 rounded-lg border border-softgray space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-navy">Planned Public Financial Disclosures</h3>
            <ul className="space-y-2.5 text-xs text-charcoal">
              {[
                { label: "Annual report", desc: "Program outcomes, total bed nights provided, and fiscal summaries", badge: "In Preparation", cls: "text-amber-800 bg-amber-100" },
                { label: "IRS Form 990", desc: "Published annually following public tax filings", badge: "Pending Tax Year Filing", cls: "text-amber-800 bg-amber-100" },
                { label: "Financial statements", desc: "Independently compiled financial reviews", badge: "Scheduled", cls: "text-amber-800 bg-amber-100" },
                { label: "Fund allocation overview", desc: "Clear accounting of restricted vs. unrestricted contributions", badge: "Active Standard", cls: "text-emerald-800 bg-emerald-100" },
              ].map((f) => (
                <li key={f.label} className="flex items-center justify-between gap-3 bg-white p-2.5 rounded-lg border border-softgray">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gold shrink-0" />
                    <span><strong>{f.label}:</strong> {f.desc}</span>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${f.cls}`}>{f.badge}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* How support is used + updates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="bg-white p-7 rounded-xl border border-softgray space-y-3">
            <h3 className="text-lg font-bold text-navy uppercase tracking-wide">How Support is Used</h3>
            <p className="text-xs sm:text-sm text-charcoal leading-relaxed">
              Donor support helps build the systems, partnerships, and operational capacity required to provide structured veteran support. As our programs develop, we will share specific funding priorities, progress milestones, and implementation updates so supporters can see what their giving helps make possible.
            </p>
          </section>
          <section className="bg-white p-7 rounded-xl border border-softgray space-y-3">
            <h3 className="text-lg font-bold text-navy uppercase tracking-wide">Updates and Reporting</h3>
            <p className="text-xs sm:text-sm text-charcoal leading-relaxed">
              We will use this website to post meaningful updates on development progress, organizational milestones, and public accountability information. Our goal is to communicate clearly, update regularly, and remain open about both progress and next steps.
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="bg-offwhite p-6 rounded-xl border border-softgray text-center space-y-3 print:hidden">
          <h3 className="text-base font-bold text-navy uppercase tracking-wide">Have Questions About Our Accountability Practices?</h3>
          <p className="text-xs text-slate max-w-xl mx-auto">
            We welcome inquiries from community partners, donors, and veteran families regarding our operational development and compliance standards.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate("/contact")} data-testid="accountability-contact-btn" className="px-5 py-2.5 bg-navy text-white font-bold text-xs uppercase tracking-widest rounded-lg hover:bg-navy-deep transition-colors flex items-center gap-2">
              Contact Governance Team <ArrowRight className="w-3.5 h-3.5 text-gold" />
            </button>
            <button onClick={() => navigate("/donate")} data-testid="accountability-donate-btn" className="px-5 py-2.5 bg-gold text-navy font-bold text-xs uppercase tracking-widest rounded-lg hover:bg-gold-deep hover:text-white transition-colors">
              Support the Mission
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accountability;
